import { useCallback } from "react";
import {
  globalAttrPatchToUpdateInput,
  globalAttributeToCreateInput,
  spaceLocalPatchToUpdateInput,
  type SavedSpaceLocalAttribute,
} from "../mappers";
import { useCreateGlobalAttribute } from "./useCreateGlobalAttribute";
import { useGlobalAttributes } from "./useGlobalAttributes";
import { usePromoteAttributeToGlobal } from "./usePromoteAttributeToGlobal";
import {
  SpaceLocalValueSetReadError,
  useSpaceLocalValueSet,
  type LiveValueSet,
} from "./useSpaceLocalValueSet";
import { useUpdateGlobalAttribute } from "./useUpdateGlobalAttribute";
import { useUpdateSpaceLocalAttribute } from "./useUpdateSpaceLocalAttribute";
import type { GlobalAttribute, SpaceLocalAttribute } from "#common/types/vault";

export interface UseGlobalAttributeAdminReturn {
  /** Presentational catalog for `DocumentTypeSettings` `globalLib`. */
  globalLib: GlobalAttribute[];
  /** Vault global `key` -> backend `attributeId` (for `useVaultSpaces` etc.). */
  globalIdByKey: Record<string, string>;
  loading: boolean;
  saving: boolean;
  /**
   * `onAddGlobalAttr` handler: mint a new steward global attribute. For select
   * types the initial value set is sent inline as `options` on the create input.
   */
  addGlobalAttr: (attr: GlobalAttribute) => Promise<void>;
  /**
   * `onSaveGlobalAttr` handler: edit name/description and append to a select
   * type's value set via `updateGlobalAttribute`. Resolves the vault `key` to a
   * backend id via the catalog; a no-op if the key is unknown.
   */
  saveGlobalAttr: (key: string, patch: Partial<GlobalAttribute>) => Promise<void>;
  /**
   * `onPromoteLocal` handler: lift a space-local attribute into the global
   * library (DV-648). `localId` is the backend attribute id the vault model
   * already carries. `newLabel` renames as part of the promotion, which is how
   * the pre-check modal clears a same-name conflict. Rejects on a server
   * refusal so the caller can keep its modal open.
   */
  promoteLocal: (localId: string, newLabel?: string) => Promise<void>;
  /**
   * `onSaveLocal` handler: edit a space-local attribute in place (DV-674) —
   * label, requirement, and appended select values. `saved` is the attribute as
   * it stood when the editor opened, which is what every field is diffed
   * against; it comes from the editor rather than from a `spaces` lookup because
   * a row on a later page of the Space-local grid may never have been in
   * `spaces` at all. When the save touches the value set, the option baseline is
   * re-read from the server first, since a stale one would delete a
   * concurrently-added option.
   *
   * Resolves `false` when the diff was empty and no mutation was sent, so the
   * caller can withhold a success toast and a grid refetch. Rejects on a server
   * refusal so the caller can keep its modal open, or with a
   * {@link SpaceLocalValueSetReadError} when the pre-save read itself failed and
   * the edit was never attempted.
   */
  saveLocal: (
    spaceId: string,
    localId: string,
    patch: Partial<SpaceLocalAttribute>,
    saved: SavedSpaceLocalAttribute,
  ) => Promise<boolean>;
}

/**
 * Container hook wiring the attribute lifecycle to the presentational
 * `DocumentTypeSettings` callbacks: global inline-create (DV-251) and edit
 * (DV-204), promotion (DV-648), and space-local edit (DV-674). Reads the
 * catalog for the `key -> backend id` correlation the global edit path needs.
 *
 * The space-local paths live here rather than in a hook of their own because
 * they share this one consumer — `VaultProvider` mounts this hook once and
 * hands every attribute callback to the settings screen from it.
 */
export const useGlobalAttributeAdmin = (): UseGlobalAttributeAdminReturn => {
  const { globalLib, globalIdByKey, loading } = useGlobalAttributes();
  const { createGlobalAttribute, loading: creating } = useCreateGlobalAttribute();
  const { updateGlobalAttribute, loading: updating } = useUpdateGlobalAttribute();
  const { promoteAttributeToGlobal, loading: promoting } = usePromoteAttributeToGlobal();
  const { updateSpaceLocalAttribute, loading: updatingLocal } = useUpdateSpaceLocalAttribute();
  const { readValueSet } = useSpaceLocalValueSet();

  const addGlobalAttr = useCallback(
    async (attr: GlobalAttribute) => {
      await createGlobalAttribute(globalAttributeToCreateInput(attr));
    },
    [createGlobalAttribute],
  );

  const saveGlobalAttr = useCallback(
    async (key: string, patch: Partial<GlobalAttribute>) => {
      const id = globalIdByKey[key];
      if (!id) return; // unknown key -> nothing to update (see catalog correlation)
      const saved = globalLib.find((attr) => attr.key === key)?.values ?? null;
      const updateInput = globalAttrPatchToUpdateInput(patch, saved);
      if (Object.keys(updateInput).length > 0) {
        await updateGlobalAttribute(id, updateInput);
      }
    },
    [globalIdByKey, globalLib, updateGlobalAttribute],
  );

  const promoteLocal = useCallback(
    async (localId: string, newLabel?: string) => {
      await promoteAttributeToGlobal(localId, newLabel);
    },
    [promoteAttributeToGlobal],
  );

  const saveLocal = useCallback(
    async (
      spaceId: string,
      localId: string,
      patch: Partial<SpaceLocalAttribute>,
      saved: SavedSpaceLocalAttribute,
    ) => {
      let input = spaceLocalPatchToUpdateInput(localId, patch, saved);

      // `options` is a full replacement set and an omitted entry is a REMOVAL,
      // so it must be rebuilt against the server's set at save time: the
      // editor's snapshot is as old as the open modal, and anything another
      // admin added since would be omitted, i.e. deleted. Only worth a
      // round-trip when the value set is actually part of this save.
      if ("options" in input && saved.slug) {
        let live: LiveValueSet | null;
        try {
          live = await readValueSet(spaceId, localId, saved.slug);
        } catch (error) {
          // Fail closed — the snapshot fallback is exactly the concurrent-delete
          // bug this read prevents — but flag it as a read failure so the caller
          // doesn't report a rejection for a mutation that never ran.
          throw new SpaceLocalValueSetReadError(error);
        }
        // A null read means the attribute is gone or no longer visible; leave
        // the snapshot-derived input alone and let the mutation report it.
        if (live) {
          input = spaceLocalPatchToUpdateInput(localId, patch, { ...saved, ...live });
        }
      }

      // `id` alone says nothing changed; sending it would burn an audit entry.
      // Reported back so the caller can skip a success toast and a grid refetch
      // for a request that was never sent.
      if (Object.keys(input).length === 1) return false;
      await updateSpaceLocalAttribute(input);
      return true;
    },
    [readValueSet, updateSpaceLocalAttribute],
  );

  return {
    globalLib,
    globalIdByKey,
    loading,
    saving: creating || updating || promoting || updatingLocal,
    addGlobalAttr,
    saveGlobalAttr,
    promoteLocal,
    saveLocal,
  };
};
